import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  AnalyticsEventName,
  pushDataLayerEvent,
} from '@components/base/GoogleTags'
import { UpdatedConstituentItem } from '@components/event/BlackBaudEventModal'
import { APPLY_EVENT_PATCH } from '@graphql/events/applyEventPatch'
import { CREATE_EVENT } from '@graphql/events/createEvent'
import { DELETE_EVENT_BY_ID } from '@graphql/events/deleteEventById'
import { DUPLICATE_EVENT_BY_ID } from '@graphql/events/duplicateEventById'
import { GET_EVENT_BY_ID_FOR_EDITOR } from '@graphql/events/GetEventByIdForEditor'
import { SYNC_EVENT_ATTENDEES_WITH_ZAPIER } from '@graphql/events/syncEventAttendeesWithZapier'
import {
  CreateEventInput,
  EventDetails,
  EventType,
  TicketType,
} from '@graphql/__generated/graphql'
import { GET_PAYMENT_SETTINGS } from '@graphql/payment/paymentSettings'
import { GET_USERS_BY_IDS } from '@graphql/users/GetUsersByIds'
import {
  CreateEventStageInfo,
  EventCreation,
  CreateEventContext,
} from '@libs/CreateEventContext'
import { useAuth } from '@libs/Auth/useAuth'
import {
  cleanEventPayload,
  cleanGqlPayload,
  debounce,
} from '@libs/Utility/util'
import {
  getValidationRules,
  validate,
  ValidationRules,
} from '@libs/Utility/validation'
import { useBlackBaud } from '@libs/useBlackBaud'
import useLocalStorage from '@libs/useLocalStorage'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import { isGoCardlessEnabled } from '@libs/paymentFeatureFlags'
import { isArray, isEqual } from 'lodash'
import moment from 'moment'
import { useRouter } from 'next/router'
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-hot-toast'
import { formatEventInstant } from '@libs/EventTime'
import { CreateEventStage } from './CreateEventStage'
import {
  applyPatchOperationsToDraft,
  buildEditableEventPayload,
  buildPatchOperations,
  canonicalizePatchValue,
  cloneJson,
  JsonPatchOperation,
  rebaseAttendeeChanges,
} from './eventPatch'

const CREATE_EVENT_STAGE_EVENTS: Partial<
  Record<CreateEventStage, AnalyticsEventName>
> = {
  [CreateEventStage.Type]: 'create_event_step_1',
  [CreateEventStage.Details]: 'create_event_step_2',
  [CreateEventStage.Communications]: 'create_event_step_3',
}

const getFormStateFromEventData = (_event?: Partial<EventCreation>) => ({
  details: true,
  registration: false,
  fundraising: false,
  speakers: false,
  stages: false,
  sessions: false,
  attendees: false,
  sponsors: false,
  onDemand: false,
  communications: false,
  breakoutRooms: false,
  notifications: false,
  emailTemplates: false,
})

const useEventEditorState = () => {
  const { publish, getEvent: getBlacBaudEvent } = useBlackBaud()
  const [blackbaudId, setBlackbaudId] = useState<string | undefined>()
  const [showBlackBaudModal, setShowBlackBaudModal] = useState<boolean>(false)
  const [duplicateLoading, setDuplicateLoading] = useState<boolean>(false)
  const [blackBaudComparison, setBlackBaudComparison] = useState<any>({
    local: null,
    remote: null,
  })
  const [baseEvent, setBaseEvent] = useState<Partial<EventCreation>>()
  const baseEventRef = useRef<Partial<EventCreation>>()
  const [formData, setFormData] = useState<Partial<EventCreation> | undefined>()
  const formDataRef = useRef<Partial<EventCreation> | undefined>()
  const hydratedEventResultRef = useRef<any>()
  const saveInFlightRef = useRef<Promise<string | undefined> | null>(null)
  const saveRequestedRef = useRef(false)
  const requestedDraftRef = useRef<Partial<EventCreation>>()
  const saveGenerationRef = useRef(0)
  const activeSaveEventIdRef = useRef<string>()
  const applyAttendeeConflictRef = useRef<() => Promise<void>>()
  const pendingAttendeeConflictRef = useRef<{
    eventId: string
    basePayload: any
  }>()
  const [validationRules, setValidationRules] = useState<ValidationRules>([])
  const [errors, setErrors] = useState<
    { step: string; message: string; eleId?: string; alert?: boolean }[]
  >([])
  const [showEmbedModal, setShowEmbedModal] = useState<boolean>(false)
  const [embedCopied, setEmbedCopied] = useState<boolean>(false)
  const [showPublishConfirmation, setShowPublishConfirmation] =
    useState<boolean>(false)
  const [showStripeRequiredModal, setShowStripeRequiredModal] =
    useState<boolean>(false)
  const [usersMetadata, setUsersData] = useState<any[]>([])
  const [loaded, setLoaded] = useState(false)
  const [pushedToBlackbaud, setPushedToBlackbaud] = useState(false)
  const [loadingBlackBaud, setLoadingBlackBaud] = useState(false)
  const [blackBaudError, setBlackbaudError] = useState(false)
  const [createEventStage, dispatchCreateEventStage] = useState<
    CreateEventStageInfo | undefined
  >({ stage: CreateEventStage.Type, skipPreview: true })
  const [completeSections, setCompleteSections] = useState({
    details: false,
    registration: false,
    speakers: false,
    fundraising: false,
    stages: false,
    sessions: false,
    attendees: false,
    sponsors: false,
    onDemand: false,
    communications: false,
    breakoutRooms: false,
    notifications: false,
    emailTemplates: false,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isZapierSyncing, setIsZapierSyncing] = useState(false)
  const [zapierSyncMessage, setZapierSyncMessage] = useState<string>()
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [saved, setSaved] = useState(false)
  const [eventTypeError, setEventTypeError] = useState<string>()
  const eventTypeSelectionInProgressRef = useRef(false)
  const isNewEventFlowRef = useRef<boolean>()
  const trackedCreateEventsRef = useRef(new Set<AnalyticsEventName>())

  const { profile, getPermission, isOrganizer, isAdmin } = useAuth()
  const { profile: organisationProfile } = useOrganisationProfile(
    profile?.company ?? profile?.university!
  )
  const router = useRouter()
  const { query, push, isReady } = router
  const eventId = useMemo(
    () => query.id ?? formData?.eventId,
    [query.id, formData?.eventId]
  )
  const urlEventId = useMemo(() => {
    if (typeof window === 'undefined') return undefined
    return new URLSearchParams(window.location.search).get('id') ?? undefined
  }, [router.asPath])
  const activeEventId =
    formData?.eventId ?? urlEventId ?? (router.query.id as string | undefined)

  const [isFirstLogin, setFirstLoginFlag] = useLocalStorage(
    'Forumm_FirstLogin',
    false
  )

  const { data: paymentSettingsData, loading: paymentSettingsLoading } =
    useQuery(GET_PAYMENT_SETTINGS, { skip: !profile?.userId })
  const paymentSettings = paymentSettingsData?.getPaymentSettings

  const hasDirtyChanges = useMemo(() => {
    if (!baseEvent || !formData) return false

    const fundraiserDefaults = {
      startDateTime: moment().toISOString(),
      registrationCloseDateTime: moment().add(14, 'd').toISOString(),
      endDateTime: moment().add(1, 'M').toISOString(),
    }

    return !isEqual(
      buildEditableEventPayload(baseEvent, profile, fundraiserDefaults),
      buildEditableEventPayload(formData, profile, fundraiserDefaults)
    )
  }, [baseEvent, formData, profile])

  const [getEventById, { data, loading, refetch }] = useLazyQuery(
    GET_EVENT_BY_ID_FOR_EDITOR,
    { fetchPolicy: 'network-only' }
  )
  const [getAttendeeMetadata] = useLazyQuery(GET_USERS_BY_IDS)
  const [createEvent] = useMutation(CREATE_EVENT)
  const [applyEventPatch] = useMutation(APPLY_EVENT_PATCH)
  const [syncEventAttendeesWithZapier] = useMutation(
    SYNC_EVENT_ATTENDEES_WITH_ZAPIER
  )
  const [deleteEventById] = useMutation(DELETE_EVENT_BY_ID)
  const [duplicateEventById] = useMutation(DUPLICATE_EVENT_BY_ID)

  const setCreateEventStage = useCallback(
    (data: Partial<CreateEventStageInfo>): void => {
      dispatchCreateEventStage((current) => ({ ...current!, ...data }))
    },
    []
  )

  const trackCreateEvent = useCallback((eventName: AnalyticsEventName) => {
    if (trackedCreateEventsRef.current.has(eventName)) return

    pushDataLayerEvent(eventName)
    trackedCreateEventsRef.current.add(eventName)
  }, [])

  const trackNewCreateEvent = useCallback(
    (eventName: AnalyticsEventName) => {
      if (!isNewEventFlowRef.current) return
      trackCreateEvent(eventName)
    },
    [trackCreateEvent]
  )

  useEffect(() => {
    if (!isReady || isNewEventFlowRef.current !== undefined) return

    isNewEventFlowRef.current = query.id == null
  }, [isReady, query.id])

  useEffect(() => {
    if (!isReady || !profile?.userId || !isNewEventFlowRef.current) return

    const stageEvent =
      CREATE_EVENT_STAGE_EVENTS[createEventStage?.stage as CreateEventStage]
    if (stageEvent) trackNewCreateEvent(stageEvent)
  }, [createEventStage?.stage, isReady, profile?.userId, trackNewCreateEvent])

  useEffect(() => {
    if (isFirstLogin) setFirstLoginFlag(false)
  }, [isFirstLogin, setFirstLoginFlag])

  useEffect(() => {
    baseEventRef.current = baseEvent
  }, [baseEvent])

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  useEffect(() => {
    if (saved) {
      setTimeout(() => {
        setSaved(false)
      }, 30000)
    }
  }, [saved])

  useEffect(() => {
    if (formData && (formData?.attendees ?? []).length > 0) {
      const userIds: string[] = (formData?.attendees ?? [])
        .filter((attendee) => !!attendee.userId || !!attendee.email)
        .map((attendee) => attendee.userId ?? attendee.email)
        .filter((id): id is string => Boolean(id))

      if (usersMetadata.length === 0)
        getAttendeeMetadata({
          variables: {
            input: {
              userIds,
            },
          },
          onCompleted: (data) => setUsersData(data.getUsersByIds.items ?? []),
        })
    }
  }, [formData?.attendees])

  useEffect(() => {
    console.log({ isOrganizer })
    if (!!profile && !isOrganizer && !isAdmin) {
      setCreateEventStage({ stage: CreateEventStage.Space })
    }
    if (createEventStage?.stage === CreateEventStage.Space && isOrganizer) {
      setCreateEventStage({ stage: CreateEventStage.Type })
    }
  }, [profile])

  useEffect(() => {
    console.log({ eventId })
  }, [eventId])

  useEffect(() => {
    if (pushedToBlackbaud) {
      toast(`Successfully Synced with BlackBaud!`, {
        icon: '🎉',
        duration: 4000,
        className: 'custom-toast',
      })
    }
  }, [pushedToBlackbaud])

  useEffect(() => {
    if (data?.getEventById?.event?.blackbaudId)
      setBlackbaudId(data?.getEventById?.event?.blackbaudId)
  }, [data?.getEventById?.event?.blackbaudId])

  useEffect(() => {
    async function getEvent(eventId: string) {
      if (eventId && (isAdmin || isOrganizer)) {
        getEventById({
          variables: { input: { eventId: eventId } },
        })
      }
    }

    if (isReady && eventId && (!loaded || formData?.eventId !== eventId)) {
      void getEvent(eventId as string)
    }
  }, [
    eventId,
    formData?.eventId,
    getEventById,
    isAdmin,
    isOrganizer,
    isReady,
    loaded,
  ])

  useEffect(() => {
    if (data?.getEventById) {
      if (hydratedEventResultRef.current === data.getEventById) return
      hydratedEventResultRef.current = data.getEventById

      const cleanPayload: Partial<EventCreation> = cleanEventPayload(
        data
      ) as EventCreation
      const isDifferentEvent = formData?.eventId !== cleanPayload.eventId
      const shouldHydrateCleanPayload =
        !formData ||
        isDifferentEvent ||
        (!hasDirtyChanges &&
          !isEqual(
            canonicalizePatchValue(formData),
            canonicalizePatchValue(cleanPayload)
          ))

      if (isReady && !loaded && cleanPayload?.event?.eventType) {
        setCreateEventStage({ stage: CreateEventStage.Details })
      }

      if (shouldHydrateCleanPayload) {
        if (isDifferentEvent) {
          saveGenerationRef.current += 1
          activeSaveEventIdRef.current = cleanPayload.eventId
          saveRequestedRef.current = false
          requestedDraftRef.current = undefined
          saveInFlightRef.current = null
        }
        baseEventRef.current = cleanPayload
        formDataRef.current = cleanPayload
        setBaseEvent(cleanPayload)
        setFormData(cleanPayload)
        setLoaded(true)
      }
    }

    if (isReady && eventId == null) {
      setLoaded(true)
    }
  }, [data, eventId, formData, hasDirtyChanges, isReady, loaded])

  const disableProgress =
    createEventStage?.stage === CreateEventStage.Type &&
    !!!formData?.event?.eventType

  const items = useMemo(() => {
    return [
      { label: 'Type', targetStage: CreateEventStage.Type },
      { label: 'Details', targetStage: CreateEventStage.Details },
      {
        label: 'Communication',
        targetStage: CreateEventStage.Communications,
      },
      { label: 'Preview', targetStage: CreateEventStage.Preview },
    ].filter(
      (item) =>
        (item.targetStage === CreateEventStage.Communications &&
          formData?.event?.eventType !== EventType.Fundraiser) ||
        item.targetStage !== CreateEventStage.Communications
    )
  }, [formData?.event?.eventType])

  useEffect(() => {
    const rules = getValidationRules(
      formData?.event?.eventType ?? undefined
    ).reduce(
      (acc: ValidationRules, val) =>
        acc.find((rule) => rule[0] === val[0] || typeof val[1] === 'function')
          ? acc
          : [...acc, val],
      []
    )
    setValidationRules(rules)
  }, [formData?.event?.eventType])

  const generateEventId = async (eventType?: EventType) => {
    const initialDateTime = moment()
    const startDateTime = initialDateTime.clone().add(1, 'd').toISOString()
    const endDateTime = initialDateTime.clone().add(5, 'd').toISOString()
    const payload: CreateEventInput = {
      event: {
        title: '',
        shortDescription: '',
        description: '',
        startDateTime,
        entryDateTime: startDateTime,
        endDateTime,
        registrationCloseDateTime: startDateTime,
        organizationName: profile?.university ?? profile?.company ?? '',
        publiclyListed: true,
        eventType: eventType ?? EventType.Online,
      },
    }

    const createdEvent = await createEvent({
      variables: {
        input: payload,
      },
    })

    const createdEventId = createdEvent.data?.createEvent?.eventId

    if (!createdEventId) return undefined

    const createdEventResponse = await getEventById({
      variables: { input: { eventId: createdEventId } },
    })
    const persistedCreatedEvent = createdEventResponse.data?.getEventById
      ? (cleanEventPayload(createdEventResponse.data as any) as EventCreation)
      : undefined
    const createdFormData = persistedCreatedEvent
      ? ({
          ...persistedCreatedEvent,
          event: {
            ...persistedCreatedEvent.event,
            title: payload.event.title || undefined,
            startDateTime: payload.event.startDateTime,
            entryDateTime: payload.event.entryDateTime,
            endDateTime: payload.event.endDateTime,
            registrationCloseDateTime: payload.event.registrationCloseDateTime,
          },
        } as Partial<EventCreation>)
      : undefined

    if (createdFormData) {
      hydratedEventResultRef.current = createdEventResponse.data?.getEventById
      saveGenerationRef.current += 1
      saveRequestedRef.current = false
      requestedDraftRef.current = undefined
      saveInFlightRef.current = null
      baseEventRef.current = createdFormData
      formDataRef.current = createdFormData
      activeSaveEventIdRef.current = createdFormData.eventId
      setBaseEvent(createdFormData)
      setFormData(createdFormData)
    }

    window.history.replaceState('', 'Create Event', `?id=${createdEventId}`)

    return createdFormData
  }

  const formState = getFormStateFromEventData(formData)

  const runPatchSave = useCallback(
    async (draft: Partial<EventCreation>) => {
      const eventId = draft.eventId ?? urlEventId ?? (router.query.id as string)
      const currentBaseEvent = baseEventRef.current
      const saveGeneration = saveGenerationRef.current

      if (!eventId || !currentBaseEvent) return undefined
      if (
        activeSaveEventIdRef.current &&
        activeSaveEventIdRef.current !== eventId
      ) {
        return undefined
      }
      activeSaveEventIdRef.current = eventId

      const fundraiserDefaults = {
        startDateTime: moment().toISOString(),
        registrationCloseDateTime: moment().add(14, 'd').toISOString(),
        endDateTime: moment().add(1, 'M').toISOString(),
      }
      const basePayload = buildEditableEventPayload(
        currentBaseEvent,
        profile,
        fundraiserDefaults
      )
      const draftPayload = buildEditableEventPayload(
        { ...draft, eventId },
        profile,
        fundraiserDefaults
      )
      const isCroppingBanner =
        typeof draftPayload?.event?.bannerImage !== 'string' &&
        !!draftPayload?.event?.bannerImage
      const isCroppingThumb =
        typeof draftPayload?.event?.thumbnailImage !== 'string' &&
        !!draftPayload?.event?.thumbnailImage
      const updateException = isCroppingBanner || isCroppingThumb
      const operations = buildPatchOperations(basePayload, draftPayload)

      if (
        updateException ||
        operations.length < 1 ||
        !draftPayload?.event?.title ||
        draftPayload?.event?.title?.length < 1 ||
        (!isAdmin && !isOrganizer)
      ) {
        return eventId
      }

      setIsLoading(true)
      try {
        const response = await applyEventPatch({
          variables: {
            input: {
              eventId,
              operations,
            },
          },
        })
        const result = response.data?.applyEventPatch

        if (
          saveGeneration !== saveGenerationRef.current ||
          activeSaveEventIdRef.current !== eventId
        ) {
          return undefined
        }

        if (!result?.success) {
          setSaved(false)
          const conflicts = result?.conflicts ?? []
          const hasOnlyAttendeeConflicts =
            conflicts.length > 0 &&
            conflicts.every(
              (conflict: { path?: string }) => conflict.path === '/attendees'
            )
          const hasAttendeePatch = operations.some(
            (operation) =>
              operation.op !== 'test' && operation.path === '/attendees'
          )

          if (hasOnlyAttendeeConflicts && hasAttendeePatch) {
            pendingAttendeeConflictRef.current = {
              eventId,
              basePayload,
            }
            toast.custom(
              (toastInstance) => (
                <div
                  className={`${
                    toastInstance.visible ? 'animate-enter' : 'animate-leave'
                  } max-w-md rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-900 shadow-lg`}
                >
                  <p className="font-medium">
                    Attendees changed while editing.
                  </p>
                  <p className="mt-1 text-gray-600">
                    Apply your attendee change to the latest attendee list?
                  </p>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded border border-gray-300 px-3 py-1 text-xs"
                      onClick={() => toast.dismiss(toastInstance.id)}
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      className="rounded bg-black px-3 py-1 text-xs text-white"
                      onClick={() => {
                        toast.dismiss(toastInstance.id)
                        void applyAttendeeConflictRef.current?.()
                      }}
                    >
                      Apply to latest
                    </button>
                  </div>
                </div>
              ),
              { duration: 15000 }
            )
            return undefined
          }

          toast.error(
            'This event changed elsewhere. Your draft has not been overwritten.'
          )
          return undefined
        }

        const savedEvent = result.event
          ? (cleanGqlPayload(result.event) as Partial<EventCreation>)
          : cloneJson({ ...draft, eventId })
        const latestFormData = formDataRef.current
        const savedDraft = cloneJson({ ...draft, eventId })

        if (savedEvent) {
          baseEventRef.current = savedEvent
          setBaseEvent(savedEvent)

          const localOperations = buildPatchOperations(
            buildEditableEventPayload(savedDraft, profile, fundraiserDefaults),
            buildEditableEventPayload(
              latestFormData,
              profile,
              fundraiserDefaults
            )
          )

          if (!latestFormData || localOperations.length === 0) {
            formDataRef.current = savedEvent
            setFormData(savedEvent)
          } else {
            const savedPayload = buildEditableEventPayload(
              savedEvent,
              profile,
              fundraiserDefaults
            )
            const rebasedPayload = applyPatchOperationsToDraft(
              savedPayload,
              localOperations
            )
            const rebasedDraft = {
              ...savedEvent,
              ...rebasedPayload,
              eventId,
            }
            formDataRef.current = rebasedDraft
            requestedDraftRef.current = rebasedDraft
            saveRequestedRef.current = true
            setFormData(rebasedDraft)
          }
        }

        setSaved(!saveRequestedRef.current)
        return result.event?.eventId ?? eventId
      } catch (error) {
        setSaved(false)
        toast.error('Unable to save this event. Your draft has been kept.')
        return undefined
      } finally {
        setIsLoading(false)
      }
    },
    [
      applyEventPatch,
      isAdmin,
      isOrganizer,
      profile,
      router.query.id,
      urlEventId,
    ]
  )

  const saveChanges = useCallback(
    (draft: Partial<EventCreation>) => {
      requestedDraftRef.current = cloneJson(draft)
      formDataRef.current = requestedDraftRef.current
      saveRequestedRef.current = true

      if (!saveInFlightRef.current) {
        const generation = saveGenerationRef.current
        const saveLoop = (async () => {
          let savedEventId: string | undefined

          while (
            saveRequestedRef.current &&
            generation === saveGenerationRef.current
          ) {
            saveRequestedRef.current = false
            const nextDraft = requestedDraftRef.current ?? formDataRef.current
            if (!nextDraft) break

            savedEventId = await runPatchSave(cloneJson(nextDraft))
            if (!savedEventId) break
          }

          return savedEventId
        })()

        saveInFlightRef.current = saveLoop
        void saveLoop.finally(() => {
          if (saveInFlightRef.current === saveLoop) {
            saveInFlightRef.current = null
          }
        })
      }

      return saveInFlightRef.current
    },
    [runPatchSave]
  )

  applyAttendeeConflictRef.current = async () => {
    const pendingConflict = pendingAttendeeConflictRef.current
    const currentDraft = formDataRef.current
    if (!pendingConflict || !currentDraft) return

    setIsLoading(true)
    try {
      const latestResponse = await refetch({
        input: { eventId: pendingConflict.eventId },
      })
      const latestEvent = cleanEventPayload(
        latestResponse.data as any
      ) as Partial<EventCreation>
      if (!latestEvent) {
        toast.error('Unable to load latest attendees.')
        return
      }

      const fundraiserDefaults = {
        startDateTime: moment().toISOString(),
        registrationCloseDateTime: moment().add(14, 'd').toISOString(),
        endDateTime: moment().add(1, 'M').toISOString(),
      }
      const currentPayload = buildEditableEventPayload(
        currentDraft,
        profile,
        fundraiserDefaults
      )
      const attendeeRebase = rebaseAttendeeChanges({
        baseAttendees: pendingConflict.basePayload.attendees,
        draftAttendees: currentPayload.attendees,
        latestAttendees: latestEvent.attendees ?? [],
      })
      if (!attendeeRebase.success) {
        toast.error(attendeeRebase.message)
        return
      }

      const nonAttendeeOperations = buildPatchOperations(
        pendingConflict.basePayload,
        currentPayload
      ).filter(
        (operation: JsonPatchOperation) => operation.path !== '/attendees'
      )
      const latestPayload = buildEditableEventPayload(
        latestEvent,
        profile,
        fundraiserDefaults
      )
      const rebasedPayload = applyPatchOperationsToDraft(
        latestPayload,
        nonAttendeeOperations
      )
      const rebasedDraft = {
        ...latestEvent,
        ...rebasedPayload,
        eventId: pendingConflict.eventId,
        attendees: attendeeRebase.attendees,
      }

      hydratedEventResultRef.current = latestResponse.data?.getEventById
      pendingAttendeeConflictRef.current = undefined
      baseEventRef.current = latestEvent
      formDataRef.current = rebasedDraft
      setBaseEvent(latestEvent)
      setFormData(rebasedDraft)
      await saveChanges(rebasedDraft)
    } catch (error) {
      toast.error('Unable to apply attendee change.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleNext = async (e: any) => {
    const hasErrors = !!errors && isArray(errors) && errors?.length > 0

    if (hasErrors || !formData) return null

    saveChanges({ ...formData })

    e.preventDefault()

    const { eventType } = formData?.event!

    const pushToEvent = () =>
      router.push(
        `event/${formData?.eventId}${
          eventType === EventType.Fundraiser ? '/donate' : ''
        }`
      )

    let nextStage = CreateEventStage.Details

    if (!formData?.event?.eventType) nextStage = CreateEventStage.Type

    if (createEventStage?.stage === CreateEventStage.Type)
      nextStage = CreateEventStage.Details

    if (createEventStage?.stage === CreateEventStage.Details)
      if (eventType === EventType.Fundraiser) {
        if (createEventStage.skipPreview) pushToEvent()
        else nextStage = CreateEventStage.Preview
      } else nextStage = CreateEventStage.Communications

    if (createEventStage?.stage === CreateEventStage.Communications) {
      if (createEventStage.skipPreview) pushToEvent()
      else nextStage = CreateEventStage.Preview
    }

    if (createEventStage?.stage === CreateEventStage.Preview) pushToEvent()

    setCreateEventStage({ stage: nextStage })
  }

  const debouncedSave = useCallback(
    debounce((input: { formData: any; errors: any }) => {
      const hasErrors =
        !!input?.errors && isArray(input?.errors) && input?.errors?.length > 0

      if (!hasErrors && !!input?.formData)
        saveChanges({
          ...input.formData,
        })
    }, 1000),
    [saveChanges]
  )

  useEffect(() => {
    debouncedSave({ formData, errors })
    const payload = { ...completeSections }

    if (formData?.event && formData?.event?.title?.length > 0)
      payload.details = true

    if (formData?.tickets && formData?.tickets?.length > 0)
      payload.registration = true

    if (formData?.speakers && formData?.speakers?.length > 0)
      payload.speakers = true

    if (formData?.fundraising && formData?.fundraising?.goal)
      payload.fundraising = true

    if (formData?.stages && formData?.stages?.length > 0) payload.stages = true

    if (formData?.sessions && formData.sessions.length > 0)
      payload.sessions = true

    if (formData?.attendees && formData.attendees.length > 0)
      payload.attendees = true

    if (formData?.sponsors && formData.sponsors.length > 0)
      payload.sponsors = true

    if (formData?.breakoutRooms && formData.breakoutRooms.length > 0)
      payload.breakoutRooms = true

    if (formData?.ondemandContent && formData.ondemandContent.length > 0)
      payload.onDemand = true

    if (formData?.notifications && formData.notifications.length > 0)
      payload.notifications = true

    setCompleteSections({ ...completeSections, ...payload })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData])

  const handleSelectEventType = async (eventType: EventType) => {
    if (eventTypeSelectionInProgressRef.current) return

    eventTypeSelectionInProgressRef.current = true
    setIsLoading(true)
    setEventTypeError(undefined)

    try {
      let payload = {
        ...formData,
        event: {
          ...formData?.event,
          eventType,
        } as EventDetails,
      }
      if (!formData?.eventId && !urlEventId && !router.query.id) {
        const createdFormData = await generateEventId(eventType)
        if (!createdFormData?.eventId) {
          throw new Error('Unable to start your event. Please try again.')
        }
        payload = {
          ...createdFormData,
          event: {
            ...createdFormData.event,
            eventType,
          } as EventDetails,
        }
      }
      formDataRef.current = payload
      setFormData(payload)
      setCreateEventStage({ stage: CreateEventStage.Details })
    } catch (error: any) {
      setEventTypeError(
        error?.message ?? 'Unable to start your event. Please try again.'
      )
    } finally {
      eventTypeSelectionInProgressRef.current = false
      setIsLoading(false)
    }
  }

  const handleZapierAttendeeSync = async () => {
    const eventIdToSync = activeEventId
    if (!eventIdToSync || !formData?.attendees?.length) return

    setIsZapierSyncing(true)
    setZapierSyncMessage(undefined)
    try {
      const savedEventId = await saveChanges({ ...formData })
      const response = await syncEventAttendeesWithZapier({
        variables: {
          eventId: savedEventId ?? eventIdToSync,
        },
      })
      const result = response.data?.syncEventAttendeesWithZapier
      if (!result?.success) {
        throw new Error(result?.error ?? 'Unable to sync attendees with Zapier')
      }
      setZapierSyncMessage(
        `Queued ${result.attendeesSynced} attendees and ` +
          `${result.attendanceUpdatesSynced} attendance updates for Zapier sync.`
      )
    } catch (error: any) {
      setZapierSyncMessage(
        error?.message ?? 'Unable to sync attendees with Zapier.'
      )
    } finally {
      setIsZapierSyncing(false)
    }
  }

  const perms = getPermission('EVENT::TYPE::*')

  const toggleFundraising = (val: boolean) =>
    setFormData({
      ...formData,
      fundraising: {
        ...formData?.fundraising,
        enabled: val,
      },
    })

  const togglePremium = (val: boolean) => {
    if (isAdmin || !!perms.find((p) => p === 'PREMIUM'))
      setFormData({
        ...formData,
        isPremium: val ?? formData?.isPremium,
      })
  }

  const goToDetailsStage = () =>
    setCreateEventStage({ stage: CreateEventStage.Details })

  const goToDashboard = () => router.push('/dashboard')

  const handlePushToCRM = async (
    selection: string[],
    updatedConstituents: UpdatedConstituentItem[] | undefined
  ) => {
    setLoadingBlackBaud(true)
    const response = await publish(
      eventId as string,
      selection,
      updatedConstituents,
      blackbaudId
    )
    const isSuccess = response?.data?.pushToBlackbaud?.success
    if (response?.data?.pushToBlackbaud?.newBlackbaudId)
      setBlackbaudId(response?.data?.pushToBlackbaud?.newBlackbaudId)
    if (isSuccess) {
      setPushedToBlackbaud(true)
    } else {
      setBlackbaudError(true)
    }
    setTimeout(() => {
      setPushedToBlackbaud(false)
      setLoadingBlackBaud(false)
    }, 1000)
  }

  const openBlackBaudModal = async () => {
    setBlackbaudError(false)
    setLoadingBlackBaud(true)

    const response = await getBlacBaudEvent(eventId as string, blackbaudId)

    if (response?.errors?.[0]?.message === 'AuthenticationError') {
      setBlackbaudError(true)
      setLoadingBlackBaud(false)
      return
    }

    const localEvent = formData?.event
    const localAttendees = formData?.attendees ?? []
    const localSpeakers = formData?.speakers ?? []
    const blackbaudEvent = response?.data?.getBlackBaudEvent

    let blackbaudData = null

    const localData = [
      { label: 'Title', content: localEvent?.title ?? '' },
      {
        label: 'Description',
        content: localEvent?.shortDescription ?? '',
      },
      {
        label: 'Start Date',
        content: localEvent?.startDateTime
          ? formatEventInstant(localEvent?.startDateTime, 'MMM DD, h:mmA', localEvent)
          : '',
      },
      {
        label: 'End Date',
        content: localEvent?.endDateTime
          ? formatEventInstant(localEvent?.endDateTime, 'MMM DD, h:mmA', localEvent)
          : '',
      },
      {
        label: 'Attendees',
        content: localAttendees
          .map((attendee) => `${attendee.name} (${attendee.email})`)
          .join(', '),
        metadata: usersMetadata || [],
      },
      {
        label: 'Speakers',
        content: localSpeakers
          .map((speaker) => `${speaker.name} (${speaker.email})`)
          .join(', '),
      },
    ]

    if (blackbaudEvent) {
      const startDateTime = `${blackbaudEvent?.start_date ?? ''} ${
        blackbaudEvent?.start_time ?? ''
      }`
      const endDateTime = `${blackbaudEvent?.end_date ?? ''} ${
        blackbaudEvent?.end_time ?? ''
      }`

      blackbaudData = [
        { label: 'Title', content: blackbaudEvent?.name ?? '' },
        {
          label: 'Description',
          content: blackbaudEvent?.description ?? '',
        },
        {
          label: 'Start Date',
          content: startDateTime
            ? moment(startDateTime).format('MMM DD, h:mmA')
            : null,
        },
        {
          label: 'End Date',
          content: endDateTime
            ? moment(endDateTime).format('MMM DD, h:mmA')
            : null,
        },
        {
          label: 'Attendees',
          content: blackbaudEvent?.attendees
            .map(
              (attendee: { name: any; email: any }) =>
                `${attendee.name} (${attendee.email})`
            )
            .join(', '),
          list: blackbaudEvent?.attendees,
        },
        {
          label: 'Speakers',
          content: blackbaudEvent?.speakers
            .map(
              (speaker: { name: any; email: any }) =>
                `${speaker.name} (${speaker.email})`
            )
            .join(', '),
          list: blackbaudEvent?.attendees,
        },
      ]
    }
    setBlackBaudComparison({
      local: localData,
      remote: blackbaudData,
    })
    setShowBlackBaudModal(true)
    setLoadingBlackBaud(false)
  }

  const handleSaveSelectionBlackBaud = async (
    selection: string[],
    updatedConstituents: UpdatedConstituentItem[] | undefined
  ) => {
    setShowBlackBaudModal(false)
    setBlackbaudError(false)
    handlePushToCRM(selection, updatedConstituents)
  }

  const alreadyOnBlackbaud = blackbaudId || pushedToBlackbaud

  const hasPaidTickets = useMemo(
    () =>
      (formData?.tickets ?? []).some(
        (ticket) =>
          ticket.ticketType === TicketType.Paid ||
          ticket.ticketType === TicketType.GroupGuest
      ),
    [formData?.tickets]
  )

  const configuredPaymentProcessor = formData?.paymentProcessor ?? 'STRIPE'
  const selectedPaymentProcessor =
    configuredPaymentProcessor === 'GOCARDLESS' && !isGoCardlessEnabled()
      ? 'PAYMENTS_DISABLED'
      : configuredPaymentProcessor
  const paymentCurrency = (formData?.event?.currency ?? 'GBP').toUpperCase()
  const goCardlessCurrencySupported = ['GBP', 'EUR'].includes(paymentCurrency)
  const selectedPaymentConnection =
    selectedPaymentProcessor === 'STRIPE'
      ? paymentSettings?.stripe
      : selectedPaymentProcessor === 'GOCARDLESS'
        ? paymentSettings?.gocardless
        : undefined
  const paymentSelectionReady =
    selectedPaymentProcessor !== 'PAYMENTS_DISABLED' &&
    !!selectedPaymentConnection?.ready &&
    (selectedPaymentProcessor !== 'GOCARDLESS' || goCardlessCurrencySupported)
  const paymentBlocksPublish = hasPaidTickets && !paymentSelectionReady
  const eventActionsVisible =
    eventId && !!data?.getEventById?.event?.eventType && !!formData?.event

  const embedBaseUrl =
    typeof window === 'undefined' ? '' : window.location.origin
  const embedEventId = Array.isArray(eventId) ? eventId[0] : eventId
  const embedContainerId = `forumm-registration-${String(
    embedEventId ?? ''
  ).replace(/[^a-zA-Z0-9_-]/g, '-')}`

  const embedCode = `<div id="${embedContainerId}"></div>

<script
  src="${embedBaseUrl}/forumm-embed.js"
  data-event-id="${embedEventId ?? ''}"
  data-container-id="${embedContainerId}"
  data-height="760">
</script>`

  const handleCopyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy embed code!', err)
    }
  }

  const handlePublishRequest = () => {
    if (formData?.isPublished) return

    const eventType: EventType = formData?.event?.eventType ?? EventType.Online
    const results = validate({ ...formData }, getValidationRules(eventType))

    if (results.error) {
      setErrors((state) => [...state, { ...results.error, alert: true }])
      return
    }

    if (paymentBlocksPublish) {
      setShowStripeRequiredModal(true)
      return
    }

    setShowPublishConfirmation(true)
  }

  const handleConfirmPublish = async () => {
    if (!eventId || (!isAdmin && !isOrganizer) || !formData) return

    const publishDraft = {
      ...formData,
      eventId: eventId as string,
      isPublished: true,
    }
    formDataRef.current = publishDraft
    setFormData(publishDraft)
    await saveChanges(publishDraft).then(async (savedEventId) => {
      if (savedEventId) {
        trackCreateEvent('create_event_published')
        await refetch()
      }
    })

    setShowPublishConfirmation(false)
  }

  const handleGoToStripeSettings = () => {
    if (embedEventId) {
      window.localStorage.setItem('Forumm_LastEventEdited', embedEventId)
    }
    push('/organisation-settings?tab=payments')
  }

  const handleDuplicateEvent = async () => {
    setDuplicateLoading(true)
    await duplicateEventById({
      variables: {
        eventId: eventId as string,
      },
    })
      .then((data) => {
        const success = data.data?.duplicateEventById?.success
        if (!!success && success === true) {
          const eventId = data?.data?.duplicateEventById.event?.eventId
          setTimeout(async () => {
            await push(`/create-event?id=${eventId}`).then(() =>
              router.reload()
            )
          }, 500)
          setDuplicateLoading(false)
        } else {
          toast(`Unexpected Error when duplicating event.`, {
            icon: '!',
            duration: 5000,
            className: 'custom-toast',
          })
        }
      })
      .finally(() => setDuplicateLoading(false))
  }

  const handleDeleteEvent = () => {
    deleteEventById({
      variables: {
        input: {
          eventId: eventId as string,
        },
      },
    })
    setTimeout(() => {
      push('/dashboard')
    }, 500)
  }

  return {
    activeEventId,
    alreadyOnBlackbaud,
    blackBaudComparison,
    blackBaudError,
    completeSections,
    createEventStage,
    disableProgress,
    duplicateLoading,
    embedCode,
    embedCopied,
    errors,
    eventTypeError,
    eventActionsVisible,
    eventId,
    formData,
    formState,
    goToDashboard,
    goToDetailsStage,
    handleConfirmPublish,
    handleCopyEmbedCode,
    handleDeleteEvent,
    handleDuplicateEvent,
    handleGoToStripeSettings,
    handleNext,
    handlePublishRequest,
    handleSaveSelectionBlackBaud,
    handleSelectEventType,
    handleZapierAttendeeSync,
    hasDirtyChanges,
    isAdmin,
    isFirstLogin,
    isLoading,
    isZapierSyncing,
    items,
    loading,
    loadingBlackBaud,
    goCardlessCurrencySupported,
    hasPaidTickets,
    openBlackBaudModal,
    organisationProfile,
    perms,
    profile,
    refetchEvent: refetch,
    saved,
    setCreateEventStage,
    setErrors,
    setFormData,
    setShowBlackBaudModal,
    setShowEmbedModal,
    setShowErrorModal,
    setShowPublishConfirmation,
    setShowStripeRequiredModal,
    showErrorModal,
    showBlackBaudModal,
    showEmbedModal,
    showPublishConfirmation,
    showStripeRequiredModal,
    paymentSelectionReady,
    paymentSettings,
    paymentSettingsLoading,
    selectedPaymentProcessor,
    toggleFundraising,
    togglePremium,
    urlEventId,
    validationRules,
    zapierSyncMessage,
  }
}

export type EventEditor = ReturnType<typeof useEventEditorState>

export const EventEditorProvider = ({ children }: { children: ReactNode }) => {
  const editor = useEventEditorState()

  return (
    <CreateEventContext.Provider value={editor}>
      {children}
    </CreateEventContext.Provider>
  )
}

export const useEventEditor = () =>
  useContext(CreateEventContext) as EventEditor
